import { useState } from "react";
import { Loader2, Printer, Mail, UserX, Send, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PurchasesTab = ({
  formData, purchaseDateFrom, setPurchaseDateFrom,
  purchaseDateTo, setPurchaseDateTo, purchaseLoading,
  purchaseHistory, handlePrintPurchases, handleReprintInvoice,
  openEmailTransactionModal, handleUnassignSale,
  loadingReprintInvoice, formatDate, customerId,
}) => {
  const [sendingBatch, setSendingBatch] = useState(false);
  const [itemSearch, setItemSearch] = useState("");

  const filteredHistory = itemSearch.trim()
    ? purchaseHistory.filter(item => 
        item.description?.toLowerCase().includes(itemSearch.toLowerCase()) ||
        item.plu?.toLowerCase().includes(itemSearch.toLowerCase()) ||
        item.inv_no?.toLowerCase().includes(itemSearch.toLowerCase())
      )
    : purchaseHistory;

  const handleBatchEmail = async () => {
    const email = formData.email;
    if (!email || !email.includes("@")) {
      toast.error("Customer has no email address. Please add one in the Profile tab.");
      return;
    }
    if (!purchaseDateFrom || !purchaseDateTo) {
      toast.error("Please select both From and To dates");
      return;
    }
    const count = new Set(purchaseHistory.map(h => h.inv_no)).size;
    if (count === 0) {
      toast.error("No invoices in this date range");
      return;
    }
    if (!window.confirm(`Email ${count} invoice(s) to ${email}?\n\nPeriod: ${purchaseDateFrom} to ${purchaseDateTo}\n\nIf this is a VAT invoice customer, tax invoices will be sent automatically.`)) return;

    setSendingBatch(true);
    try {
      const res = await axios.post(`${API}/email/batch-invoices`, {
        customer_id: customerId,
        recipient_email: email,
        date_from: purchaseDateFrom,
        date_to: purchaseDateTo,
      });
      toast.success(`Sent ${res.data.invoices_sent} ${res.data.invoice_type} (R${res.data.total_amount.toFixed(2)}) to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoices");
    } finally {
      setSendingBatch(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with customer info and date filters */}
      <div className="flex items-center justify-between bg-blue-800 text-white p-3 rounded-t">
        <div>
          <h3 className="font-bold">Purchase History</h3>
          <p className="text-sm text-blue-200">{formData.name} | {formData.contact_number}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm">From</span>
          <Input
            type="date"
            value={purchaseDateFrom}
            onChange={(e) => setPurchaseDateFrom(e.target.value)}
            className="w-36 h-8 text-black"
          />
          <span className="text-sm">To</span>
          <Input
            type="date"
            value={purchaseDateTo}
            onChange={(e) => setPurchaseDateTo(e.target.value)}
            className="w-36 h-8 text-black"
          />
          <Button variant="secondary" size="sm" onClick={handlePrintPurchases} title="Print">
            <Printer className="h-4 w-4" />
          </Button>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={handleBatchEmail}
            disabled={sendingBatch}
            title="Email all invoices in this period"
            data-testid="batch-email-invoices-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {sendingBatch ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="ml-1 text-xs">Email All</span>
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
        <Input
          value={itemSearch}
          onChange={(e) => setItemSearch(e.target.value)}
          placeholder="Search items by name, PLU, or invoice number..."
          className="pl-9 h-9"
          data-testid="purchase-search-input"
        />
        {itemSearch && (
          <span className="absolute right-3 top-2 text-xs text-gray-400">
            {filteredHistory.length} of {purchaseHistory.length} items
          </span>
        )}
      </div>

      {/* Purchase History Table */}
      <div className="border rounded overflow-auto max-h-[400px]">
        {purchaseLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 bg-blue-700 text-white">
              <TableRow>
                <TableHead className="text-white font-bold">InvNo</TableHead>
                <TableHead className="text-white font-bold">Date</TableHead>
                <TableHead className="text-white font-bold">PLU</TableHead>
                <TableHead className="text-white font-bold">Description</TableHead>
                <TableHead className="text-white font-bold text-center">Qty</TableHead>
                <TableHead className="text-white font-bold text-right">Retail</TableHead>
                <TableHead className="text-white font-bold text-right">Total</TableHead>
                <TableHead className="text-white font-bold">Type</TableHead>
                <TableHead className="text-white font-bold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    {itemSearch ? `No items matching "${itemSearch}"` : "No purchase history found for this period"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredHistory.map((item, idx) => {
                  const isFirstOfInvoice = idx === 0 || filteredHistory[idx - 1]?.inv_no !== item.inv_no;
                  
                  return (
                    <TableRow key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <TableCell className="font-mono">{item.inv_no}</TableCell>
                      <TableCell>{formatDate(item.date)}</TableCell>
                      <TableCell className="font-mono">{item.plu}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell className="text-center">{item.qty}</TableCell>
                      <TableCell className="text-right font-mono">{item.retail?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-mono">{item.total?.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.sale_type?.includes('ACCOUNT') ? 'bg-blue-100 text-blue-700' :
                          item.sale_type?.includes('CASH') ? 'bg-green-100 text-green-700' :
                          item.sale_type?.includes('CARD') ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {item.sale_type?.replace(' SALE', '') || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {isFirstOfInvoice && item.sale_id ? (
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleReprintInvoice(item)}
                              disabled={loadingReprintInvoice === item.inv_no}
                              title="Reprint Invoice"
                              data-testid={`reprint-invoice-${item.inv_no}`}
                            >
                              {loadingReprintInvoice === item.inv_no ? (
                                <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
                              ) : (
                                <Printer className="h-4 w-4 text-purple-600" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => openEmailTransactionModal(item.sale_id, item.inv_no)}
                              title="Email Invoice"
                              data-testid={`email-invoice-${item.inv_no}`}
                            >
                              <Mail className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleUnassignSale(item.sale_id, item.inv_no)}
                              title="Remove from customer (convert to Cash Sale)"
                              data-testid={`unassign-sale-${item.inv_no}`}
                            >
                              <UserX className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
